<template>
  <div
    v-if="visible"
    class="voting-dialog-backdrop"
    role="presentation"
    @click.self="close"
  >
    <section
      class="voting-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="voting-dialog-title"
    >
      <header class="voting-dialog__header">
        <div>
          <p class="voting-dialog__eyebrow">Última revisión</p>
          <h2 id="voting-dialog-title">Confirma tu boleta</h2>
        </div>
        <button type="button" class="voting-dialog__close" aria-label="Cerrar" @click="close">
          &times;
        </button>
      </header>

      <div class="voting-dialog__notice">
        <span class="voting-dialog__notice-icon" aria-hidden="true">&#10003;</span>
        <p>
          Revisa tus selecciones. Al confirmar, tu voto será definitivo y no podrá modificarse.
        </p>
      </div>

      <dl class="voting-dialog__summary">
        <div v-for="item in selectionSummary" :key="item.contestKey">
          <dt>{{ item.contestTitle }}</dt>
          <dd>{{ item.labels.join(", ") || "Abstención" }}</dd>
        </div>
      </dl>

      <footer class="voting-dialog__actions">
        <button type="button" class="voting-button voting-button--secondary" :disabled="submitting" @click="close">
          Revisar
        </button>
        <button type="button" class="voting-button voting-button--primary" :disabled="submitting" @click="$emit('confirm')">
          <span v-if="submitting" class="voting-button__spinner" aria-hidden="true"></span>
          <span v-else aria-hidden="true">&#10003;</span>
          {{ submitting ? "Enviando..." : "Confirmar y enviar" }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
export default {
  name: "VotingConfirmDialog",
  props: {
    visible: { type: Boolean, default: false },
    selectionSummary: { type: Array, default: () => [] },
    submitting: { type: Boolean, default: false },
  },
  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    close() {
      if (!this.submitting) this.$emit("close");
    },
    handleKeydown(event) {
      if (this.visible && event.key === "Escape") this.close();
    },
  },
};
</script>

<style scoped>
.voting-dialog-backdrop {
  position: fixed;
  z-index: 300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(10, 18, 32, 0.62);
}

.voting-dialog {
  width: min(100%, 560px);
  max-height: calc(100vh - 40px);
  overflow: auto;
  border-radius: 20px;
  padding: 24px;
  background: #ffffff;
  box-shadow: 0 24px 70px rgba(10, 18, 32, 0.28);
}

.voting-dialog__header,
.voting-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.voting-dialog__eyebrow {
  margin: 0 0 4px;
  color: #a66f00;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.voting-dialog h2 {
  margin: 0;
  color: #1b273e;
  font-size: 24px;
}

.voting-dialog__close {
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: #f1f4f8;
  color: #1b273e;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}

.voting-dialog__notice {
  display: flex;
  gap: 10px;
  margin-top: 22px;
  border-radius: 12px;
  padding: 14px;
  background: #eef3fb;
  color: #1b273e;
}

.voting-dialog__notice p {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
}

.voting-dialog__notice-icon {
  display: flex;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #1b273e;
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
}

.voting-dialog__summary {
  margin: 18px 0 24px;
  overflow: hidden;
  border: 1px solid #dfe4eb;
  border-radius: 12px;
}

.voting-dialog__summary div {
  padding: 13px 15px;
}

.voting-dialog__summary div + div {
  border-top: 1px solid #dfe4eb;
}

.voting-dialog__summary dt {
  color: #687386;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.voting-dialog__summary dd {
  margin: 5px 0 0;
  color: #1b273e;
  font-size: 14px;
  font-weight: 800;
}

.voting-dialog__actions {
  justify-content: flex-end;
  flex-wrap: wrap;
}

.voting-button {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
}

.voting-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.voting-button--secondary {
  border: 1px solid #cbd3df;
  background: #ffffff;
  color: #1b273e;
}

.voting-button--primary {
  background: #ffb500;
  color: #1b273e;
}

.voting-button__spinner {
  width: 15px;
  height: 15px;
  border: 2px solid rgba(27, 39, 62, 0.35);
  border-top-color: #1b273e;
  border-radius: 50%;
  animation: voting-spin 700ms linear infinite;
}

@keyframes voting-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 560px) {
  .voting-dialog {
    padding: 20px;
  }

  .voting-dialog__actions,
  .voting-button {
    width: 100%;
  }
}
</style>
